import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Spinner,
  Table,
} from "react-bootstrap";
import { Download, HouseAdd, PersonPlus, Search } from "react-bootstrap-icons";
import api from "../api/api";
import { ADMIN_PANEL_URL } from "../constants";
import { addTrialDays } from "../utils/trialPolicy";

import ModeSwitch from "./ModeSwitch";
import ClinicOrganisationModal from "./ClinicOrganisationModal";
import "../css/ManageUsers.css";

const USERS_PER_PAGE = 25;
const subscriptionLabels = { active: 'Active', trialing: 'In trial', sponsored: 'Sponsored', cancelled: 'Cancelled' };
const dateLabel = (value) => value ? new Date(value.length === 10 ? `${value}T12:00:00` : value).toLocaleDateString('en-GB') : '—';
const today = () => new Date().toISOString().slice(0, 10);

const createEmptyTherapist = () => ({
  first_name: "",
  last_name: "",
  email: "",
  discipline: "",
  clinic: "",
  is_clinic_manager: false,
  billing_mode: "clinic",
  use_therapist_override: false,
  billing_state: "trialing",
  start_date: today(),
  trial_end_date: addTrialDays(today()),
});

const ManageUsers = () => {
  const [view, setView] = useState('clinics');
  const [subscriptionFilter, setSubscriptionFilter] = useState('active');
  const [userRole, setUserRole] = useState('therapist');
  const [users, setUsers] = useState([]);
  const [organisationClinic, setOrganisationClinic] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMethod, setAddMethod] = useState("individual");
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [formMessage, setFormMessage] = useState({ type: "", text: "" });

  const [newTherapist, setNewTherapist] = useState(createEmptyTherapist);

  const [csvFile, setCsvFile] = useState(null);

  const requestIdRef = useRef(0);
  const currentPageRef = useRef(1);
  const searchTermRef = useRef("");
  const userRoleRef = useRef('therapist');

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const visibleClinics = useMemo(() => clinics.filter((clinic) => {
    const cancelled = clinic.subscription_lifecycle?.billing_state === 'cancelled';
    return (subscriptionFilter === 'cancelled' ? cancelled : !cancelled)
      && clinic.name.toLowerCase().includes(searchTerm.trim().toLowerCase());
  }), [clinics, searchTerm, subscriptionFilter]);
  const totalItems = view === 'clinics' ? visibleClinics.length : totalUsers;
  const totalPages = Math.max(1, Math.ceil(totalItems / USERS_PER_PAGE));
  const clinicRows = visibleClinics.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);
  useEffect(() => {
    if (view === 'clinics' && currentPage > totalPages) setCurrentPage(totalPages);
  }, [view, currentPage, totalPages]);

  const selectedClinic = useMemo(
    () =>
      clinics.find(
        (clinic) => clinic.id === parseInt(newTherapist.clinic, 10)
      ) || null,
    [clinics, newTherapist.clinic]
  );

  const selectedClinicLifecycle = selectedClinic?.subscription_lifecycle || null;
  const needsLifecycleFields =
    newTherapist.billing_mode === "clinic" &&
    (newTherapist.use_therapist_override ||
      (selectedClinic && !selectedClinicLifecycle));

  const buildSearchableText = useCallback((user) => {
    return `${user.first_name || ""} ${user.last_name || ""} ${user.email || ""} ${
      user.is_staff ? "staff" : user.discipline || "client"
    }`.toLowerCase();
  }, []);

  const fetchClinics = useCallback(async () => {
    const clinicsResponse = await api.get("/clinics/");
    setClinics(clinicsResponse.data);
  }, []);

  const fetchUsersPage = useCallback(
    async ({ page, term, role } = {}) => {
      const pageToLoad = page ?? currentPageRef.current;
      const search = term ?? searchTermRef.current;

      const myRequestId = ++requestIdRef.current;

      setUsersLoading(true);
      try {
        const response = await api.get("/users/", {
          params: {
            page: pageToLoad,
            page_size: USERS_PER_PAGE,
            role: role ?? userRoleRef.current,
            ...(search.trim() ? { search: search.trim() } : {}),
          },
        });

        if (myRequestId !== requestIdRef.current) return;

        const results = response.data?.results ?? [];
        const count = response.data?.count ?? 0;

        const processedUsers = results.map((user) => ({
          ...user,
          searchableText: buildSearchableText(user),
        }));

        setUsers(processedUsers);
        setTotalUsers(count);
      } catch (error) {
        if (myRequestId !== requestIdRef.current) return;

        console.error("Failed to fetch users:", error);
        setMessage({
          type: "danger",
          text: "Failed to fetch users. Please try again later.",
        });
      } finally {
        if (myRequestId === requestIdRef.current) {
          setUsersLoading(false);
        }
      }
    },
    [buildSearchableText]
  );

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchClinics();
      } catch (error) {
        console.error("Failed to load initial data:", error);
        setMessage({
          type: "danger",
          text: "Failed to load data. Please refresh the page or try again later.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [fetchClinics]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
    currentPageRef.current = 1;
    setSelectedUsers([]);
    if (view !== 'users') {
      requestIdRef.current += 1;
      setUsersLoading(false);
      return;
    }
    setUsers([]);
    fetchUsersPage({ page: 1, term: searchTerm, role: userRole });
  }, [view, userRole, searchTerm, fetchUsersPage]);

  const changeView = (next) => {
    setView(next); setSearchInput(''); setSearchTerm(''); setSelectedUsers([]);
  };
  const changeRole = (role) => {
    userRoleRef.current = role;
    setUserRole(role); setSelectedUsers([]);
  };

  const handleSearch = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleUserSelect = useCallback((userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId);
      return [...prev, userId];
    });
  }, []);

  const handleSelectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        setSelectedUsers(users.map((user) => user.id));
      } else {
        setSelectedUsers([]);
      }
    },
    [users]
  );

  const goToPage = useCallback(
    (page) => {
      const nextPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(nextPage);
      setSelectedUsers([]);
      if (view === 'users') fetchUsersPage({ page: nextPage, term: searchTermRef.current });
    },
    [fetchUsersPage, totalPages, view]
  );

  const handleDeactivateUsers = useCallback(async () => {
    const validUserIds = selectedUsers.filter((userId) =>
      users.some((user) => user.id === userId)
    );

    if (validUserIds.length === 0) {
      setMessage({
        type: "danger",
        text: "No valid users selected for deactivation.",
      });
      return;
    }

    try {
      await api.post("/users/deactivate/", { userIds: validUserIds });

      setMessage({
        type: "success",
        text: `${validUserIds.length} user(s) deactivated successfully.`,
      });

      setShowDeactivateModal(false);
      setSelectedUsers([]);

      await fetchUsersPage({ page: currentPageRef.current, term: searchTermRef.current });
    } catch (error) {
      console.error("Failed to deactivate users:", error);
      setMessage({
        type: "danger",
        text:
          error.response?.data?.error ||
          "Failed to deactivate users. Please try again.",
      });
    }
  }, [selectedUsers, users, fetchUsersPage]);

  const handleAddTherapist = useCallback(async () => {
    try {
      setFormMessage({ type: "", text: "" });

      if (addMethod === "individual") {
        const requiredFields = {
          clinic: newTherapist.clinic,
          first_name: newTherapist.first_name?.trim(),
          last_name: newTherapist.last_name?.trim(),
          email: newTherapist.email?.trim(),
          discipline: newTherapist.discipline,
        };

        const missingFields = Object.entries(requiredFields)
          .filter(([, value]) => !value)
          .map(([key]) => key.replace("_", " "));

        if (missingFields.length > 0) {
          setFormMessage({
            type: "danger",
            text: `Please fill in all required fields: ${missingFields.join(", ")}.`,
          });
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(requiredFields.email)) {
          setFormMessage({
            type: "danger",
            text: "Please enter a valid email address.",
          });
          return;
        }

        const validClinic = clinics.find(
          (clinic) => clinic.id === parseInt(requiredFields.clinic)
        );
        if (!validClinic) {
          setFormMessage({
            type: "danger",
            text: "Please select a valid clinic.",
          });
          return;
        }

        const needsLifecycleInput =
          newTherapist.billing_mode === "clinic" &&
          (newTherapist.use_therapist_override ||
            !validClinic.subscription_lifecycle);

        if (needsLifecycleInput) {
          const lifecycleMissing = [];

          if (!newTherapist.start_date) {
            lifecycleMissing.push("start date");
          }
          if (!newTherapist.billing_state) {
            lifecycleMissing.push("billing status");
          }
          if (
            newTherapist.billing_state === "trialing" &&
            !newTherapist.trial_end_date
          ) {
            lifecycleMissing.push("trial end date");
          }

          if (lifecycleMissing.length > 0) {
            setFormMessage({
              type: "danger",
              text: `Please fill in: ${lifecycleMissing.join(", ")}.`,
            });
            return;
          }

          if (
            newTherapist.start_date &&
            newTherapist.trial_end_date &&
            newTherapist.trial_end_date < newTherapist.start_date
          ) {
            setFormMessage({
              type: "danger",
              text: "Trial end date cannot be before the start date.",
            });
            return;
          }
        }

        await api.post("/users/add_therapist/", {
          first_name: requiredFields.first_name,
          last_name: requiredFields.last_name,
          email: requiredFields.email,
          clinic_id: parseInt(requiredFields.clinic),
          discipline: requiredFields.discipline,
          is_clinic_manager: newTherapist.is_clinic_manager,
          billing_mode: newTherapist.billing_mode,
          use_therapist_override: newTherapist.use_therapist_override,
          ...(needsLifecycleInput
            ? {
                start_date: newTherapist.start_date,
                billing_state: newTherapist.billing_state,
                trial_end_date:
                  newTherapist.billing_state === "trialing"
                    ? newTherapist.trial_end_date
                    : "",
              }
            : {}),
        });

        setFormMessage({
          type: "success",
          text: "Therapist added successfully.",
        });

        setTimeout(() => {
          setShowAddModal(false);
          setFormMessage({ type: "", text: "" });
          setNewTherapist(createEmptyTherapist());
        }, 1200);
      } else {
        if (!csvFile) {
          setFormMessage({
            type: "danger",
            text: "Please select a CSV file to upload.",
          });
          return;
        }

        if (!csvFile.name.toLowerCase().endsWith(".csv")) {
          setFormMessage({
            type: "danger",
            text: "Please upload a valid CSV file.",
          });
          return;
        }

        if (csvFile.size > 5 * 1024 * 1024) {
          setFormMessage({
            type: "danger",
            text: "File size must be less than 5MB.",
          });
          return;
        }

        const formData = new FormData();
        formData.append("file", csvFile);

        await api.post("/users/add_therapist_csv/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setFormMessage({
          type: "success",
          text: "Therapists imported successfully.",
        });

        setTimeout(() => {
          setShowAddModal(false);
          setFormMessage({ type: "", text: "" });
          setCsvFile(null);
        }, 1200);
      }

      await Promise.all([
        fetchUsersPage({ page: currentPageRef.current, term: searchTermRef.current }),
        fetchClinics(),
      ]);
    } catch (error) {
      console.error("Failed to add therapist(s):", error);
      const responseData = error.response?.data;
      const fieldError =
        responseData &&
        typeof responseData === "object" &&
        Object.values(responseData).flat().find((value) => typeof value === "string");

      setFormMessage({
        type: "danger",
        text:
          responseData?.error ||
          fieldError ||
          "Failed to add therapist(s). Please try again.",
      });
    }
  }, [addMethod, newTherapist, csvFile, clinics, fetchUsersPage, fetchClinics]);

  const downloadTemplate = useCallback(() => {
    const template = "First Name,Last Name,Email,Profession,Clinic ID,Clinic Manager\n";
    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "therapist_template.csv";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, []);

  return (
    <Container className="py-4 mb-5">
      {message.text && (
        <Alert
          variant={message.type}
          onClose={() => setMessage({ type: "", text: "" })}
          dismissible
        >
          {message.text}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading administration…</p>
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
            <h2 className="mb-0">Administration</h2>
            {view === 'clinics' ? (
              <Button onClick={() => (window.location.href = `${ADMIN_PANEL_URL}/api/clinic/add/`)}><HouseAdd className="me-2" />Add Clinic</Button>
            ) : userRole === 'therapist' && (
              <Button onClick={() => setShowAddModal(true)}><PersonPlus className="me-2" />Add Therapists</Button>
            )}
          </div>
          <ModeSwitch id="admin-view" label="Admin view" left="Clinics" right="Users" checked={view === 'users'} onChange={(checked) => changeView(checked ? 'users' : 'clinics')} />
          {view === 'clinics' && <ModeSwitch id="admin-subscriptions" label="Subscription filter" left="Active" right="Cancelled" checked={subscriptionFilter === 'cancelled'} onChange={(checked) => { setSubscriptionFilter(checked ? 'cancelled' : 'active'); setCurrentPage(1); currentPageRef.current = 1; }} />}
          {view === 'users' && <ModeSwitch id="admin-user-type" label="User type" left="Therapists & staff" right="Clients" checked={userRole === 'client'} onChange={(checked) => changeRole(checked ? 'client' : 'therapist')} />}
          <InputGroup className="mb-3">
            <InputGroup.Text><Search /></InputGroup.Text>
            <Form.Control aria-label={view === 'clinics' ? 'Search clinics' : 'Search users'} placeholder={view === 'clinics' ? 'Search clinics…' : 'Search users…'} value={searchInput} onChange={handleSearch} />
          </InputGroup>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <span className="text-muted small">{totalItems} {view === 'clinics' ? 'clinics' : userRole === 'client' ? 'clients' : 'therapists & staff'} · Newest first{usersLoading && view === 'users' && <Spinner size="sm" className="ms-2" />}</span>
            {view === 'users' && selectedUsers.length > 0 && <Button size="sm" variant="outline-danger" onClick={() => setShowDeactivateModal(true)}>Deactivate selected ({selectedUsers.length})</Button>}
          </div>
          {view === 'clinics' ? <Table striped bordered hover responsive className="admin-clinic-table">
            <thead><tr><th>Clinic</th><th>Created</th><th>Subscription</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {clinicRows.map((clinic) => {
                const cycle = clinic.subscription_lifecycle;
                const expired = cycle?.billing_state === 'trialing' && clinic.trial_summary?.company === 'expired';
                const label = expired ? 'Trial expired' : subscriptionLabels[cycle?.billing_state] || 'Not set';
                return <tr key={clinic.id}>
                  <td>{clinic.name}</td><td data-label="Created" className="text-nowrap">{dateLabel(clinic.created_at)}</td>
                  <td data-label="Subscription"><Badge bg={cycle?.billing_state === 'active' ? 'success' : cycle?.billing_state === 'trialing' ? 'warning' : 'secondary'} text={cycle?.billing_state === 'trialing' ? 'dark' : undefined}>{label}</Badge>
                    {cycle?.billing_state === 'trialing' && cycle.trial_ends_at && <div className="small text-muted text-nowrap mt-1">Ends {dateLabel(cycle.trial_ends_at)}</div>}
                  </td>
                  <td>{clinic.can_manage_organisation && <Button size="sm" variant="outline-primary" aria-label={`Manage ${clinic.name}`} onClick={() => setOrganisationClinic(clinic)}>Manage</Button>}</td>
                </tr>;
              })}
              {!clinicRows.length && <tr><td colSpan={4} className="text-center text-muted py-4">No clinics found.</td></tr>}
            </tbody>
          </Table> : <Table striped bordered hover responsive>
            <thead><tr>
              <th><Form.Check type="checkbox" aria-label="Select all users" onChange={handleSelectAll} checked={selectedUsers.length === users.length && users.length > 0} /></th>
              <th>Name</th><th>Email</th><th>Role</th><th>Clinic</th>
            </tr></thead>
            <tbody>
              {users.map((user) => <tr key={user.id}>
                <td><Form.Check type="checkbox" aria-label={`Select ${user.first_name} ${user.last_name}`} checked={selectedUsers.includes(user.id)} onChange={() => handleUserSelect(user.id)} /></td>
                <td>{`${user.first_name} ${user.last_name}`.trim()}</td><td>{user.email}</td>
                <td>{user.is_staff ? 'Staff' : user.discipline || 'Client'}</td><td>{user.clinic || '—'}</td>
              </tr>)}
              {!users.length && <tr><td colSpan={5} className="text-center text-muted py-4">{usersLoading ? 'Loading users…' : 'No users found.'}</td></tr>}
            </tbody>
          </Table>}
          {totalPages > 1 && <div className="d-flex justify-content-center mt-3">
            <Pagination className="flex-wrap">
              <Pagination.First onClick={() => goToPage(1)} disabled={currentPage === 1} />
              <Pagination.Prev onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} />
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                return <Pagination.Item key={pageNumber} active={currentPage === pageNumber} onClick={() => goToPage(pageNumber)}>{pageNumber}</Pagination.Item>;
              })}
              <Pagination.Next onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} />
              <Pagination.Last onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
          </div>}

        </>
      )}

      {organisationClinic && <ClinicOrganisationModal clinic={organisationClinic} clinics={clinics} onClose={() => setOrganisationClinic(null)} onSaved={() => {
        if (view === 'users') fetchUsersPage();
        fetchClinics().catch(() => setMessage({ type: 'danger', text: 'Changes saved, but clinic details could not refresh. Please reload the page.' }));
      }} />}
      <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deactivation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to deactivate the following users?</p>
          <ul>
            {selectedUsers.map((userId) => {
              const user = users.find((u) => u.id === userId);
              return user ? (
                <li key={userId}>
                  {`${user.first_name} ${user.last_name}`.trim()} ({user.email})
                </li>
              ) : null;
            })}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeactivateModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeactivateUsers}>
            Deactivate
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setFormMessage({ type: "", text: "" });
        }}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Therapist</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-4">
            <div className="btn-group w-100">
              <Button
                variant={addMethod === "individual" ? "primary" : "outline-primary"}
                onClick={() => {
                  setAddMethod("individual");
                  setFormMessage({ type: "", text: "" });
                }}
              >
                Add Individual
              </Button>
              <Button
                variant={addMethod === "csv" ? "primary" : "outline-primary"}
                onClick={() => {
                  setAddMethod("csv");
                  setFormMessage({ type: "", text: "" });
                }}
              >
                Import CSV
              </Button>
            </div>
          </div>

          {addMethod === "individual" ? (
            <Form>
              <Form.Group className="mb-3" controlId="therapist-clinic">
                <Form.Label>Clinic</Form.Label>
                <Form.Select
                  value={newTherapist.clinic}
                  onChange={(e) =>
                    setNewTherapist({
                      ...newTherapist,
                      clinic: e.target.value,
                      use_therapist_override: false,
                      is_clinic_manager: false,
                      billing_state: "trialing",
                      start_date: today(),
                      trial_end_date: addTrialDays(today()),
                    })
                  }
                  required
                >
                  <option value="">Select Clinic</option>
                  {clinics.map((clinic) => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="new-therapist-first">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={newTherapist.first_name}
                  onChange={(e) =>
                    setNewTherapist({ ...newTherapist, first_name: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="new-therapist-last">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={newTherapist.last_name}
                  onChange={(e) =>
                    setNewTherapist({ ...newTherapist, last_name: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="new-therapist-email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={newTherapist.email}
                  onChange={(e) =>
                    setNewTherapist({ ...newTherapist, email: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="new-therapist-discipline">
                <Form.Label>Profession</Form.Label>
                <Form.Select
                  value={newTherapist.discipline}
                  onChange={(e) =>
                    setNewTherapist({ ...newTherapist, discipline: e.target.value })
                  }
                  required
                >
                  <option value="">Select Profession</option>
                  <option value="Physiotherapist">Physiotherapist</option>
                  <option value="Occupational Therapist">Occupational Therapist</option>
                  <option value="Speech Pathologist">Speech Pathologist</option>
                  <option value="Exercise Physiologist">Exercise Physiologist</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Form.Group>

              <Form.Check type="switch" role="switch" className="admin-switch" id="new-therapist-manager" label="Clinic manager"
                checked={newTherapist.is_clinic_manager}
                disabled={!selectedClinic?.can_manage_organisation}
                onChange={(event) => setNewTherapist({ ...newTherapist, is_clinic_manager: event.target.checked })} />
              <p className="small text-muted mt-1">The first therapist added to a new clinic becomes its manager automatically.</p>
              <hr className="my-4" />

              <h5>Company subscription</h5>

              {newTherapist.billing_mode === "clinic" &&
                selectedClinic &&
                selectedClinicLifecycle && (
                  <Alert variant="light" className="border">
                    <div className="fw-semibold">{selectedClinic.name} subscription</div>
                    <div className="small text-muted">
                      Status:{" "}
                      <span className="text-capitalize">
                        {selectedClinicLifecycle.billing_state}
                      </span>
                      {" · "}
                      Start: {selectedClinicLifecycle.started_at}
                      {selectedClinicLifecycle.trial_ends_at
                        ? " · Trial end: " + selectedClinicLifecycle.trial_ends_at
                        : ""}
                    </div>
                  </Alert>
                )}

              {newTherapist.billing_mode === "clinic" &&
                selectedClinic &&
                !selectedClinicLifecycle && (
                  <Alert variant="info">
                    This clinic does not have subscription details yet. The details
                    below will become the clinic default for future therapists.
                  </Alert>
                )}

              {needsLifecycleFields && (
                <>
                  <Form.Group className="mb-3" controlId="billing-status">
                    <Form.Label>Billing status</Form.Label>
                    <Form.Select
                      value={newTherapist.billing_state}
                      onChange={(e) =>
                        setNewTherapist({
                          ...newTherapist,
                          billing_state: e.target.value,
                          trial_end_date:
                            e.target.value === "trialing"
                              ? addTrialDays(newTherapist.start_date || today())
                              : "",
                        })
                      }
                      required
                    >
                      <option value="trialing">Trialing</option>
                      <option value="active">Active</option>
                      <option value="sponsored">Sponsored</option>
                      <option value="cancelled">Cancelled</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="lifecycle-start-date">
                    <Form.Label>Start date (onboarding / trial start)</Form.Label>
                    <Form.Control
                      type="date"
                      value={newTherapist.start_date}
                      onChange={(e) =>
                        setNewTherapist({
                          ...newTherapist,
                          start_date: e.target.value,
                          trial_end_date:
                            newTherapist.billing_state === "trialing"
                              ? addTrialDays(e.target.value)
                              : newTherapist.trial_end_date,
                        })
                      }
                      required
                    />
                  </Form.Group>

                  {newTherapist.billing_state === "trialing" && (
                    <Form.Group className="mb-3" controlId="trial-end-date">
                      <Form.Label>Trial end date</Form.Label>
                      <Form.Control
                        type="date"
                        value={newTherapist.trial_end_date}
                        min={newTherapist.start_date || undefined}
                        onChange={(e) =>
                          setNewTherapist({
                            ...newTherapist,
                            trial_end_date: e.target.value,
                          })
                        }
                        required
                      />
                      <Form.Text>
                        Defaults to 2 weeks after the start date. You can adjust
                        this for an agreed company trial.
                      </Form.Text>
                    </Form.Group>
                  )}
                </>
              )}
            </Form>
          ) : (
            <div>
              <div className="text-center mb-3">
                <Button variant="outline-secondary" onClick={downloadTemplate}>
                  <Download className="me-2" />
                  Download Template
                </Button>
              </div>

              <p className="small text-muted">Clinic Manager: Y or N. If no manager is selected and the clinic has none, the first imported therapist alphabetically by first name, then last name, becomes manager.</p>
              <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>Upload CSV File</Form.Label>
                <Form.Control
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="flex-column align-items-stretch">
          {formMessage.text && (
            <Alert
              variant={formMessage.type}
              onClose={() => setFormMessage({ type: "", text: "" })}
              dismissible
              className="mb-2"
              role={formMessage.type === "success" ? "status" : "alert"}
              aria-live={formMessage.type === "success" ? "polite" : undefined}
            >
              {formMessage.text}
            </Alert>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddModal(false);
                setFormMessage({ type: "", text: "" });
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddTherapist}>
              {addMethod === "individual" ? "Add Therapist" : "Import Therapists"}
            </Button>
          </div>

          <p className="text-muted text-center mb-0 mt-2">
            New therapists will be automatically be sent an activation email
          </p>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ManageUsers;
